"use client";
import { IndexCards } from "@/components/IndexCards";
import { FC, useContext } from "react";
import { IndexCardsContext } from "./IndexContextBlock";
import { Analytics, Card as CardType } from "@/types/General";
import { BuilderProps, SpecificFields } from "@/types";
import { Spinner } from "@/icons";
import { cardMapper } from "./cardMapper";
import { FilteringFunction, FilteringObject } from "@/types/builder/Filters";

type MasonryLayout = {
  layoutType: "masonry";
  numberOfColumns: 2;
};

type GridLayout = {
  layoutType: "grid";
  numberOfColumns: 2 | 4;
};

type StackLayout = {
  layoutType: "stack";
};
type LayoutType = MasonryLayout | GridLayout | StackLayout;

type InfiniteScroll = {
  type: "infinite-scroll";
};

type ExpandableSection = {
  type: "expandable-section";
  numberOfInitialItems?: number;
  viewMoreText: { text: string } & Analytics;
  viewLessText: { text: string } & Analytics;
};

type PaginationType = { paginationType: InfiniteScroll | ExpandableSection };

type Section = {
  text: string;
  cardFilter?: FilteringObject;
  orderCardsByTitle?: string;
} & LayoutType;

type IndexCardsBlockProps = {
  sections: Section[];
} & PaginationType &
  Pick<
    BuilderProps,
    | "builderBlock"
    | "builderComponents"
    | "builderContext"
    | "builderLinkComponent"
  > & {
    specificConfiguration?: SpecificFields;
    notFound: {
      isDisplayed: boolean;
      message: string;
      suggestion: string;
    };
  };
export const IndexCardsBlock: FC<IndexCardsBlockProps> = (props) => {
  const {
    numberOfPaginatedCards,
    numberOfResults,
    isLoading,
    displayedCards,
    hiddenCards,
    searchExpression,
    onGoToNextPage,
    page,
    onGoToFirstPage,
    onGoToLastPage,
  } = useContext(IndexCardsContext);
  const remappedDisplayedCards = displayedCards
    ?.map((card) =>
      cardMapper(
        card,
        props.specificConfiguration,
        props.builderContext?.rootState?.locale as string | undefined,
      ),
    )
    .filter(
      (card): card is NonNullable<typeof card> =>
        card !== null && card !== undefined,
    );
  const remappedHiddenCards = hiddenCards
    ?.map((card) =>
      cardMapper(
        card,
        props.specificConfiguration,
        props.builderContext?.rootState?.locale as string | undefined,
      ),
    )
    .filter(
      (card): card is NonNullable<typeof card> =>
        card !== null && card !== undefined,
    );

  if (!numberOfResults && props.notFound?.isDisplayed && !isLoading) {
    const stripped = props.notFound.message.replace("{keyword}", "");
    const keywordIndex = props.notFound.message.indexOf("{keyword}");
    const [firstPart, secondPart] = [
      stripped.slice(0, keywordIndex),
      stripped.slice(keywordIndex),
    ];
    return (
      <>
        <p className="text-body-m text-swdc-black-600 mb-4">
          {firstPart}
          <strong>{searchExpression}</strong>
          {secondPart}
        </p>
        <div
          className="builder-text text-body-m text-swdc-gray-400"
          dangerouslySetInnerHTML={{ __html: props.notFound.suggestion }}
        />
      </>
    );
  }

  const sectionsWithPredicate = props.sections.map((section) => ({
    ...section,
    predicate: compilePredicateFunction(section.cardFilter),
    orderCardsByTitle: parseOrderCardsByTitle(section.orderCardsByTitle),
  }));

  switch (props.paginationType.type) {
    case "infinite-scroll": {
      return (
        <IndexCards
          paginationType="infinite-scroll"
          displayedCards={remappedDisplayedCards}
          hiddenCards={remappedHiddenCards}
          isEndOfPage={page * numberOfPaginatedCards >= numberOfResults}
          isLoading={isLoading}
          loadingComponent={
            <div className="mx-auto mt-8 w-fit animate-spin">
              <Spinner />
            </div>
          }
          onGoToNextPage={onGoToNextPage}
          sections={sectionsWithPredicate}
        />
      );
    }
    case "expandable-section": {
      return (
        <IndexCards
          paginationType="expandable-section"
          displayedCards={remappedDisplayedCards}
          hiddenCards={remappedHiddenCards}
          sections={sectionsWithPredicate}
          viewLessText={props.paginationType.viewLessText}
          viewMoreText={props.paginationType.viewMoreText}
          onGoToFirstPage={onGoToFirstPage}
          onGoToLastPage={onGoToLastPage}
          isEndOfPage={numberOfResults < numberOfPaginatedCards}
          numberOfResults={numberOfResults}
          isLoading={isLoading}
          loadingComponent={
            <div className="mx-auto mt-8 w-fit animate-spin">
              <Spinner />
            </div>
          }
        />
      );
    }
  }
};

function normalizeValue(value: string) {
  switch (value) {
    case "true":
      return true;
    case "false":
      return false;
    case "null":
      return null;
    case "undefined":
      return undefined;
    default:
      return value;
  }
}

function compilePredicateFunction(
  cardFilter?: FilteringObject,
): FilteringFunction | undefined {
  if (!cardFilter || !cardFilter.type) {
    return undefined;
  }

  if (cardFilter.type === "custom") {
    return cardFilter.callback;
  }

  if (cardFilter.type === "Categorization") {
    const matchesCategorization = (card: CardType) =>
      card.categorization?.some(
        (categorization) =>
          categorization.parameterName === cardFilter.parameterName &&
          categorization.categories.includes(cardFilter.value),
      ) ?? false;

    return cardFilter.operand === "EQUAL"
      ? matchesCategorization
      : (card: CardType) => !matchesCategorization(card);
  }

  const getFieldValue = (card: CardType): unknown => {
    if (
      cardFilter.type === "CVE" &&
      cardFilter.field === "severity" &&
      card.type === "CVE"
    ) {
      const categories = card.categorization?.find(
        (categorization) => categorization.parameterName === "severity",
      )?.categories;
      return (Array.isArray(categories) ? categories[0] : categories) ?? "N/A";
    }

    if (cardFilter.field === "isFeaturedAndDate") {
      const isFeatured = card.isFeatured ?? false;
      const hasDate = Boolean(
        ("startDate" in card && card.startDate) ||
        ("endDate" in card && card.endDate) ||
        ("date" in card && card.date),
      );
      return isFeatured && hasDate;
    }

    return card[cardFilter.field as keyof CardType];
  };

  if (cardFilter.operand === "EQUAL") {
    return (card: CardType) =>
      getFieldValue(card) === normalizeValue(cardFilter.value);
  }

  if (cardFilter.operand === "NOT EQUAL") {
    return (card: CardType) =>
      getFieldValue(card) !== normalizeValue(cardFilter.value);
  }

  return undefined;
}

function parseOrderCardsByTitle(value?: string) {
  const titles =
    value
      ?.split(",")
      .map((title) => title.trim())
      .filter(Boolean) ?? [];

  return titles.length > 0 ? titles : undefined;
}
